import { World } from "./world";
import type { Command, Order } from "./commands";
import type { Sim } from "./sim";
import { UNIT_DEFS } from "../data";
import { KIND_BUILDING } from "./constants";
import { canPlace, placePos, nearestFreeSpot } from "./placement";
import { bindExtractorToSpot } from "./economy";

// Translate external Commands into per-entity Orders / factory-queue changes.
// This is the sole entry point for player + AI intent into the sim. `sim` is
// accepted for symmetry with the rest of the pipeline (currently unused here).
export function applyCommands(w: World, _sim: Sim, cmds: Command[]): void {
  for (const cmd of cmds) applyOne(w, cmd);
}

function pushOrder(w: World, e: number, order: Order, queue: boolean): void {
  if (!queue) {
    w.orders[e].length = 0;
    w.targetEnt[e] = -1;
  }
  w.orders[e].push(order);
}

function applyOne(w: World, cmd: Command): void {
  switch (cmd.t) {
    case "move":
      for (const e of cmd.ents) {
        if (!w.isAlive(e)) continue;
        pushOrder(w, e, { kind: "move", x: cmd.x, y: cmd.y, attackMove: cmd.attackMove }, cmd.queue);
      }
      break;
    case "attack":
      for (const e of cmd.ents) {
        if (!w.isAlive(e)) continue;
        pushOrder(w, e, { kind: "attack", target: cmd.target }, cmd.queue);
      }
      break;
    case "attackGround":
      for (const e of cmd.ents) {
        if (!w.isAlive(e)) continue;
        pushOrder(w, e, { kind: "attackGround", x: cmd.x, y: cmd.y }, cmd.queue);
      }
      break;
    case "reclaim":
      for (const e of cmd.ents) {
        if (!w.isAlive(e) || w.def(e).buildPower <= 0) continue;
        pushOrder(w, e, { kind: "reclaim", target: cmd.target }, cmd.queue);
      }
      break;
    case "repair":
      // Only builders can repair/resume, and only a friendly, non-neutral target.
      // The build-vs-heal decision is made live in movement as the builder works,
      // so the target's state can change (e.g. construction finishing) mid-order.
      for (const e of cmd.ents) {
        if (!w.isAlive(e) || w.def(e).buildPower <= 0) continue;
        if (!w.isAlive(cmd.target) || w.owner[cmd.target] !== w.owner[e]) continue;
        pushOrder(w, e, { kind: "repair", target: cmd.target }, cmd.queue);
      }
      break;
    case "guard":
      for (const e of cmd.ents) {
        if (!w.isAlive(e)) continue;
        pushOrder(w, e, { kind: "guard", target: cmd.target }, cmd.queue);
      }
      break;
    case "stop":
      for (const e of cmd.ents) {
        if (!w.isAlive(e)) continue;
        w.orders[e].length = 0;
      }
      break;
    case "selfDestruct":
      for (const e of cmd.ents) {
        if (!w.isAlive(e)) continue;
        w.health[e] = 0;
      }
      break;
    case "build":
      applyBuild(w, cmd);
      break;
    case "factoryQueue": {
      const f = cmd.factory;
      if (!w.isAlive(f)) break;
      const fdef = w.def(f);
      if (!fdef.buildOptions?.includes(cmd.defId)) break;
      for (let i = 0; i < cmd.count; i++) w.factoryQueue[f].push(cmd.defId);
      break;
    }
    case "factoryCancel": {
      const f = cmd.factory;
      if (!w.isAlive(f)) break;
      const q = w.factoryQueue[f];
      for (let i = q.length - 1; i >= 0; i--) {
        if (q[i] === cmd.defId) {
          q.splice(i, 1);
          break;
        }
      }
      break;
    }
    case "factoryRally": {
      const f = cmd.factory;
      if (!w.isAlive(f)) break;
      w.rallyX[f] = cmd.x;
      w.rallyY[f] = cmd.y;
      break;
    }
  }
}

function applyBuild(w: World, cmd: Command & { t: "build" }): void {
  const builders = cmd.ents.filter((e) => w.isAlive(e) && w.def(e).buildPower > 0);
  if (builders.length === 0) return;
  const def = UNIT_DEFS[cmd.defId];
  if (!def || def.kind !== "building") return;
  // The commanding builder's owner owns the structure.
  const owner = w.owner[builders[0]];
  // Verify this builder can build it.
  if (!w.def(builders[0]).buildOptions?.includes(cmd.defId)) return;
  if (!canPlace(w, cmd.defId, cmd.x, cmd.y)) return;

  const pos = placePos(w, cmd.defId, cmd.x, cmd.y);
  const ghost = w.spawn(cmd.defId, owner, pos.x, pos.y, { underConstruction: true });
  if (ghost < 0) return;
  w.kind[ghost] = KIND_BUILDING;
  // Reserve the metal spot immediately so two extractors can't claim it.
  if (def.role === "extractor") {
    const s = nearestFreeSpot(w, pos.x, pos.y);
    if (s >= 0) {
      w.metalSpotIdx[ghost] = s;
      w.metalSpotOccupant[s] = ghost;
    }
    bindExtractorToSpot(w, ghost);
  }
  for (const e of builders) {
    pushOrder(w, e, { kind: "build", defId: cmd.defId, x: pos.x, y: pos.y, buildEnt: ghost }, cmd.queue);
  }
}
